import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import WebApp from "@twa-dev/sdk";
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from "firebase/auth";
import { firebaseAuth } from "../../utils/firebase";
import type { AccountInfo } from "../../types";
import { createLinkRequest, declineLoginRequest, linkFirebase, linkTelegram, pollLoginRequest } from "../../utils/api";
import { Banner, Icon } from "../common/ui";

/**
 * «Способы входа»: Telegram and Google/Apple as one account.
 *
 * Linking always proves the other identity, in whichever way this place can:
 * inside Telegram the Telegram one directly (initData) and Google through the
 * real browser; in the browser Google directly (popup) and Telegram through
 * the bot. If that identity already has its own BeeApp account, the server
 * merges it into this one (services/account_merge.py).
 */
type Wait = { kind: "link_telegram" | "link_google"; code: string; check: string; link?: string };

function errorKey(e: unknown): string {
  const m = String(e).match(/ALREADY_LINKED|BOTH_HAVE_TELEGRAM|BOTH_HAVE_GOOGLE/);
  return m ? "linked.errConflict" : "linked.errGeneric";
}

export default function LinkedAccounts({ account, token, onLinked }: {
  account: AccountInfo;
  token: string;
  onLinked: () => void;
}) {
  const { t } = useTranslation();
  const inTelegram = !!WebApp.initData?.trim();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [wait, setWait] = useState<Wait | null>(null);

  // Mini App → Google needs the link ready BEFORE the tap (Telegram opens
  // external links only straight from a tap).
  const [ready, setReady] = useState<Wait | null>(null);
  const needGoogleFromTelegram = inTelegram && !account.google_linked;
  const prepare = useCallback(() => {
    createLinkRequest("link_google", token)
      .then((r) => setReady({ kind: "link_google", code: r.code, check: r.check }))
      .catch(() => setReady(null));
  }, [token]);
  useEffect(() => {
    if (!needGoogleFromTelegram) return;
    prepare();
    const id = setInterval(prepare, 8 * 60 * 1000);
    return () => clearInterval(id);
  }, [needGoogleFromTelegram, prepare]);

  const onLinkedRef = useRef(onLinked);
  onLinkedRef.current = onLinked;
  useEffect(() => {
    if (!wait) return;
    let stopped = false;
    const id = setInterval(async () => {
      try {
        const r = await pollLoginRequest(wait.code);
        if (stopped || r.status !== "linked") return;
        stopped = true;
        setWait(null);
        setDone(true);
        onLinkedRef.current();
      } catch (e) {
        if (!stopped && String(e).includes("410")) { stopped = true; setWait(null); setError(t("auth.loginExpired")); }
      }
    }, 2500);
    return () => { stopped = true; clearInterval(id); };
  }, [wait, t]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try { await fn(); setDone(true); onLinked(); }
    catch (e: any) {
      if (!["auth/popup-closed-by-user", "auth/cancelled-popup-request"].includes(e?.code)) setError(t(errorKey(e)));
    }
    finally { setBusy(false); }
  };

  const telegram = () => {
    if (inTelegram) { run(() => linkTelegram(WebApp.initData, token)); return; }
    setError(null);
    const win = window.open("", "_blank");
    createLinkRequest("link_telegram", token)
      .then((r) => {
        setWait({ kind: "link_telegram", code: r.code, check: r.check, link: r.link });
        if (win && r.link) win.location.href = r.link; else win?.close();
      })
      .catch((e) => { win?.close(); setError(t(errorKey(e))); });
  };

  const firebase = (provider: "google" | "apple") => {
    if (inTelegram) {
      if (!ready) { prepare(); return; }
      const url = `${window.location.origin}/?approve=${encodeURIComponent(ready.code)}`;
      try { WebApp.openLink(url); } catch { window.open(url, "_blank", "noopener"); }
      setWait(ready);
      setReady(null);
      return;
    }
    run(async () => {
      const p = provider === "google" ? new GoogleAuthProvider() : new OAuthProvider("apple.com");
      const r = await signInWithPopup(firebaseAuth, p);
      await linkFirebase(await r.user.getIdToken(), token);
    });
  };

  const cancel = () => {
    if (wait) declineLoginRequest(wait.code).catch(() => {});
    setWait(null);
    if (needGoogleFromTelegram) prepare();
  };

  const row = (icon: string, label: string, linked: boolean, detail: string | null, action: React.ReactNode) => (
    <div className="linked-row">
      <span className="linked-row__icon"><Icon name={icon} size={17} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="t-sm" style={{ fontWeight: 700, display: "block" }}>{label}</span>
        <span className="t-xs muted-2" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
          {linked ? (detail || "") : t("linked.notLinked")}
        </span>
      </span>
      {linked ? <span className="badge badge-success"><Icon name="check" size={11} stroke={2.6} /> {t("linked.linked")}</span> : action}
    </div>
  );

  return (
    <div className="glass glass-lg" style={{ padding: 20 }}>
      <p className="eyebrow" style={{ marginBottom: 4 }}>{t("linked.eyebrow")}</p>
      <p className="t-xs muted-2" style={{ margin: "0 0 12px", lineHeight: 1.45 }}>{t("linked.hint")}</p>

      {wait ? (
        <div className="cross-login" role="status">
          <p className="t-sm" style={{ margin: "0 0 8px" }}>
            {wait.kind === "link_telegram" ? t("linked.waitTelegram") : t("linked.waitGoogle")}
          </p>
          <div className="cross-login__check">{wait.check}</div>
          {wait.link && (
            <a className="btn btn-primary btn-block" href={wait.link} target="_blank" rel="noopener noreferrer"
              style={{ textDecoration: "none", marginTop: 10 }}>
              <Icon name="send" size={16} /> {t("auth.openTelegram")}
            </a>
          )}
          <p className="t-xs muted-2" style={{ margin: "10px 0 0", display: "flex", gap: 8, justifyContent: "center", alignItems: "center" }}>
            <span className="spinner" style={{ width: 14, height: 14 }} /> {t("auth.waiting")}
          </p>
          <button className="btn btn-glass btn-block" style={{ marginTop: 10 }} onClick={cancel}>{t("auth.cancel")}</button>
        </div>
      ) : (
        <div className="stack" style={{ "--gap": "10px" } as React.CSSProperties}>
          {row("send", "Telegram", !!account.telegram_linked, account.username ? `@${account.username}` : null,
            <button className="btn btn-glass" style={{ padding: "8px 12px" }} onClick={telegram} disabled={busy}>
              <Icon name="link" size={14} /> {t("linked.link")}
            </button>)}
          {row("globe", "Google / Apple", !!account.google_linked, account.email,
            inTelegram ? (
              <button className="btn btn-glass" style={{ padding: "8px 12px" }} onClick={() => firebase("google")} disabled={busy}>
                <Icon name="link" size={14} /> {t("linked.link")}
              </button>
            ) : (
              <span className="row" style={{ gap: 6 }}>
                <button className="btn btn-glass" style={{ padding: "8px 12px" }} onClick={() => firebase("google")} disabled={busy}>Google</button>
                <button className="btn btn-glass" style={{ padding: "8px 12px" }} onClick={() => firebase("apple")} disabled={busy}>Apple</button>
              </span>
            ))}
        </div>
      )}
      {done && <div style={{ marginTop: 12 }}><Banner kind="success">{t("linked.done")}</Banner></div>}
      {error && <div style={{ marginTop: 12 }}><Banner kind="error">{error}</Banner></div>}
    </div>
  );
}
